// Avatar-specific utility functions for converting grid layout to absolute positioning

import { RoomPlayer } from '@airhead-games/shared/types';

/**
 * Absolute position coordinates
 */
export interface AbsolutePosition {
  x: number;
  y: number;
}

/**
 * Grid layout configuration
 */
export interface GridConfig {
  containerWidth: number;
  containerHeight: number;
  avatarSize: number;
  rowGap: number;
  columnGap: number;
  gridColumns: number;
  gridRows: number;
}

/**
 * Player position with metadata
 */
export interface PlayerPosition {
  player: RoomPlayer;
  position: AbsolutePosition;
  gridIndex: number;
}

/**
 * Calculate absolute positions for all players in a standard grid layout
 *
 * @param players - Array of players to position
 * @param config - Grid configuration
 * @returns Array of player positions
 */
export function calculateStandardGridPositions(
  players: RoomPlayer[],
  config: GridConfig
): PlayerPosition[] {
  const { containerWidth, avatarSize, rowGap, columnGap, gridColumns } = config;

  // Calculate starting position to center the grid
  const totalGridWidth =
    gridColumns * avatarSize + (gridColumns - 1) * columnGap;
  const startX = (containerWidth - totalGridWidth) / 2;
  const startY = 0; // Start from top

  return players.map((player, index) => {
    const row = Math.floor(index / gridColumns);
    const col = index % gridColumns;

    const x = startX + col * (avatarSize + columnGap);
    const y = startY + row * (avatarSize + rowGap);

    return {
      player,
      position: { x, y },
      gridIndex: index,
    };
  });
}

/**
 * Calculate grid positions with center-out arrangement
 *
 * @param players - Array of players to position
 * @param config - Grid configuration
 * @returns Array of player positions in center-out order
 */
export function calculateGridPositions(
  players: RoomPlayer[],
  config: GridConfig
): PlayerPosition[] {
  const {
    containerWidth,
    avatarSize,
    rowGap,
    columnGap,
    gridColumns,
    gridRows,
  } = config;

  if (players.length === 0) {
    return [];
  }

  // Calculate starting position to center the grid
  const totalGridWidth =
    gridColumns * avatarSize + (gridColumns - 1) * columnGap;
  const startX = (containerWidth - totalGridWidth) / 2;
  const startY = 0; // Start from top (could be centered vertically if needed)

  // Generate center-out mapping (same logic as original AvatarGrid)
  const centerRow = Math.floor((gridRows - 1) / 2);
  const centerCol = Math.floor((gridColumns - 1) / 2);

  // Generate all grid positions sorted by distance from center
  const allGridPositions: Array<{
    row: number;
    col: number;
    distance: number;
    gridIndex: number;
  }> = [];

  for (let row = 0; row < gridRows; row++) {
    for (let col = 0; col < gridColumns; col++) {
      const distance = Math.abs(row - centerRow) + Math.abs(col - centerCol);
      allGridPositions.push({
        row,
        col,
        distance,
        gridIndex: row * gridColumns + col,
      });
    }
  }

  // Sort by distance from center (closest first)
  allGridPositions.sort((a, b) => {
    if (a.distance !== b.distance) return a.distance - b.distance;
    // For same distance, prefer balanced positions
    const aBalance = Math.abs(a.row - centerRow) - Math.abs(a.col - centerCol);
    const bBalance = Math.abs(b.row - centerRow) - Math.abs(b.col - centerCol);
    return Math.abs(aBalance) - Math.abs(bBalance);
  });

  // Map players to center-out positions
  return players.map((player, playerIndex) => {
    if (playerIndex >= allGridPositions.length) {
      // Fallback to end of grid if more players than positions
      const lastPos = allGridPositions[allGridPositions.length - 1];
      return {
        player,
        position: {
          x: startX + lastPos.col * (avatarSize + columnGap),
          y: startY + lastPos.row * (avatarSize + rowGap),
        },
        gridIndex: lastPos.gridIndex,
      };
    }

    const gridPos = allGridPositions[playerIndex];
    const x = startX + gridPos.col * (avatarSize + columnGap);
    const y = startY + gridPos.row * (avatarSize + rowGap);

    return {
      player,
      position: { x, y },
      gridIndex: gridPos.gridIndex,
    };
  });
}

/**
 * Calculate absolute positions for players based on layout type and position
 *
 * @param players - Array of players to position
 * @param config - Grid configuration
 * @param layoutType - Type of layout to use
 * @param position - Position of the grid within the container
 * @param edgePadding - Padding from container edges
 * @returns Array of player positions
 */
export function calculatePlayerPositions(
  players: RoomPlayer[],
  config: GridConfig,
  layoutType: 'auto' | 'square' | 'row' | 'column' | 'grid' = 'auto',
  position: 'top' | 'bottom' | 'left' | 'right' | 'center' = 'center',
  edgePadding: number = 40
): PlayerPosition[] {
  if (players.length === 0) {
    return [];
  }

  // First calculate positions using the layout type
  let basePositions: PlayerPosition[];
  switch (layoutType) {
    case 'grid':
      basePositions = calculateGridPositions(players, config);
      break;
    case 'auto':
    case 'square':
    case 'row':
    case 'column':
    default:
      basePositions = calculateStandardGridPositions(players, config);
      break;
  }

  // Calculate grid dimensions for position-based offsets
  const {
    containerWidth,
    containerHeight,
    avatarSize,
    rowGap,
    columnGap,
    gridColumns,
    gridRows,
  } = config;

  // If position is center, apply proper centering offsets
  if (position === 'center') {
    // Calculate grid dimensions for center positioning
    const totalGridWidth =
      gridColumns * avatarSize + (gridColumns - 1) * columnGap;
    const totalGridHeight = gridRows * avatarSize + (gridRows - 1) * rowGap;

    // Calculate center offsets
    const centerOffsetX = (containerWidth - totalGridWidth) / 2;
    const centerOffsetY = (containerHeight - totalGridHeight) / 2;

    // Apply center offsets to all positions
    return basePositions.map((playerPos) => {
      const gridRow = Math.floor(playerPos.gridIndex / gridColumns);
      const gridCol = playerPos.gridIndex % gridColumns;

      return {
        ...playerPos,
        position: {
          x: centerOffsetX + gridCol * (avatarSize + columnGap),
          y: centerOffsetY + gridRow * (avatarSize + rowGap),
        },
      };
    });
  }

  const totalGridWidth =
    gridColumns * avatarSize + (gridColumns - 1) * columnGap;
  const totalGridHeight = gridRows * avatarSize + (gridRows - 1) * rowGap;

  // Calculate position-based offsets
  let offsetX = 0;
  let offsetY = 0;

  switch (position) {
    case 'top':
      offsetX = (containerWidth - totalGridWidth) / 2; // Center horizontally
      offsetY = edgePadding; // Top with padding
      break;
    case 'bottom':
      offsetX = (containerWidth - totalGridWidth) / 2; // Center horizontally
      offsetY = containerHeight - totalGridHeight - edgePadding; // Bottom with padding
      break;
    case 'left':
      offsetX = edgePadding; // Left with padding
      offsetY = (containerHeight - totalGridHeight) / 2; // Center vertically
      break;
    case 'right':
      offsetX = containerWidth - totalGridWidth - edgePadding; // Right with padding
      offsetY = (containerHeight - totalGridHeight) / 2; // Center vertically
      break;
  }

  // Apply offsets to all positions
  // For base positions, remove any existing centering and apply our positioning
  return basePositions.map((playerPos) => {
    // Start from 0,0 instead of using the base centered positions
    const gridRow = Math.floor(playerPos.gridIndex / gridColumns);
    const gridCol = playerPos.gridIndex % gridColumns;

    return {
      ...playerPos,
      position: {
        x: offsetX + gridCol * (avatarSize + columnGap),
        y: offsetY + gridRow * (avatarSize + rowGap),
      },
    };
  });
}

/**
 * Get empty slot positions for grid layout (for rendering placeholders)
 *
 * @param playerPositions - Existing player positions
 * @param config - Grid configuration
 * @returns Array of empty slot positions
 */
export function getEmptySlotPositions(
  playerPositions: PlayerPosition[],
  config: GridConfig
): AbsolutePosition[] {
  const {
    containerWidth,
    avatarSize,
    rowGap,
    columnGap,
    gridColumns,
    gridRows,
  } = config;

  const totalGridWidth =
    gridColumns * avatarSize + (gridColumns - 1) * columnGap;
  const startX = (containerWidth - totalGridWidth) / 2;
  const startY = 0;

  const occupiedIndices = new Set(playerPositions.map((p) => p.gridIndex));
  const emptyPositions: AbsolutePosition[] = [];

  for (let index = 0; index < gridColumns * gridRows; index++) {
    if (!occupiedIndices.has(index)) {
      const row = Math.floor(index / gridColumns);
      const col = index % gridColumns;

      emptyPositions.push({
        x: startX + col * (avatarSize + columnGap),
        y: startY + row * (avatarSize + rowGap),
      });
    }
  }

  return emptyPositions;
}
